import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthContextAPI, AuthCredential, User, UserSignup } from "./types";
import { useNavigate } from "react-router-dom";
import authService from "./services";
import {
  getCookieValue,
  removeCookie,
  setAuthToken,
  setCookie,
} from "../../config/Api";
const AuthContext = createContext<AuthContextAPI>({} as AuthContextAPI);
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState(() => getCookieValue("token"));
  const [user, setUser] = useState<User>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(Boolean(token));
  const navigate = useNavigate();
  useEffect(() => {
    const controller = new AbortController();
    setAuthToken(token || null);
    if (!token) {
      setUser(undefined);
      setInitializing(false);
      return;
    }
    setInitializing(true);
    authService
      .getCurrentUser(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setUser(res.user);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setUser(undefined);
          setAuthToken(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setInitializing(false);
      });
    return () => controller.abort();
  }, [token]);
  const login = useCallback(
    (credential: AuthCredential) => {
      setLoading(true);
      setError(undefined);
      authService
        .login(credential)
        .then((res) => {
          if (res.accessToken) {
            setCookie(res.accessToken);
            setAuthToken(res.accessToken);
            setInitializing(res.accessToken !== token);
            setToken(res.accessToken);
          }
          navigate("/");
        })
        .catch((err) => setError(String(err)))
        .finally(() => setLoading(false));
    },
    [navigate, token]
  );
  const register = useCallback(
    (newUser: UserSignup) => {
      setError(undefined);
      if (newUser.password !== newUser.passwordConfirmation) {
        setError("Xác nhận mật khẩu chưa đúng");
        return;
      }
      setLoading(true);
      authService
        .register(newUser)
        .then(() => navigate("/login"))
        .catch((err) => setError(String(err)))
        .finally(() => setLoading(false));
    },
    [navigate]
  );
  const changePassword = useCallback(
    (credential: UserSignup) => {
      if (!user?._id) return;
      setLoading(true);
      setError(undefined);
      authService
        .changePassword({ ...credential, _id: user._id })
        .catch((err) => setError(String(err)))
        .finally(() => setLoading(false));
    },
    [user?._id]
  );
  const logout = useCallback(() => {
    removeCookie();
    setAuthToken(null);
    setToken("");
    setUser(undefined);
    setInitializing(false);
    navigate("/");
  }, [navigate]);
  const value = useMemo(
    () => ({
      user,
      error,
      loading,
      initializing,
      login,
      register,
      changePassword,
      logout,
    }),
    [
      user,
      error,
      loading,
      initializing,
      login,
      register,
      changePassword,
      logout,
    ]
  );
  // Public content does not wait for an unrelated authentication round-trip.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
export const useAuth = () => useContext(AuthContext);
export default AuthProvider;
